import { Button } from "@/ui/button";
import { ButtonGroup, ButtonGroupSeparator } from "@/ui/button-group";
import { ICONES, Icone, s } from "../visual.jsx";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";

/**
 * A barra do modo seleção — o gesto de encaminhar várias mensagens de uma vez.
 *
 * Existe porque encaminhar era de uma em uma: o cliente manda o print, o áudio e
 * o número do processo em três mensagens, e passar isso para o advogado custava
 * três aberturas do mesmo modal. A rota já aceitava lista
 * (`POST /api/leads/:id/encaminhar` com `mensagens: []`) — o que faltava era a
 * tela saber pedir mais de uma.
 *
 * Nasce por cima da conversa e some no Esc: quem entra sem querer sai sem
 * aprender nada. Enquanto está de pé, clique na bolha marca em vez de abrir.
 */
export default function SelecaoDeMensagens() {
  const { selecionadas, wa } = useEstado();
  const { sairDaSelecao, abrirEncaminharVarias, apagarVarias, copiarVarias, juntarEmPdf, pedirDasSelecionadas } = useAcoes();
  const pode = usePode();
  if (!selecionadas) return null;

  const quantas = selecionadas.length;
  /* Capacidade do provedor E permissão de quem opera: encaminhar e apagar saem
     para o WhatsApp do cliente, copiar fica no computador de quem clicou. */
  const podeMandar = pode("leads:mensagem");
  const podeApagar = Boolean(wa?.capacidades?.apagar_para_todos) && podeMandar;
  const podeAssistente = pode("automacao:agentes");

  return (
    <div
      className="absolute top-2 left-1/2 z-[8] flex max-w-[calc(100%-24px)] -translate-x-1/2 items-center gap-2 rounded-cartao bg-sidebar py-[7px] pr-[9px] pl-[13px] text-sidebar-foreground shadow-[0_14px_34px_-12px_color-mix(in_oklab,_var(--color-foreground)_55%,_transparent)]"
      style={s("animation:vUp var(--t-curta) var(--e-entra) both")}
    >
      <span className="flex-none text-corpo font-bold tracking-[.02em] tabular-nums">
        {quantas === 0 ? "Toque nas mensagens" : `${quantas} selecionada${quantas > 1 ? "s" : ""}`}
      </span>

      {/*
        `ButtonGroup` pelo papel de grupo (é uma barra de ferramentas, e o leitor
        de tela precisa saber que os botões andam juntos), mas com o raio e a
        folga do desenho de volta: o preset cola os botões num bloco único de
        bordas partilhadas, e aqui são alvos de 36px separados sobre a barra
        escura — o alvo colado no vizinho é o toque errado no celular.
      */}
      <ButtonGroup className="gap-2 *:data-slot:rounded-controle!">
        <Acao rotulo="Copiar o texto das selecionadas" icone={ICONES.documento} desligado={!quantas} aoTocar={copiarVarias} />
        {/* O trecho inteiro vai ao assistente: o motivo numa mensagem, a hora na
            outra — é assim que "liga depois das 17" vira compromisso com o que
            foi combinado escrito dentro. */}
        {podeAssistente && (
          <Acao
            rotulo="Pedir ao assistente sobre as selecionadas"
            icone={ICONES.assistente}
            desligado={!quantas}
            aoTocar={pedirDasSelecionadas}
          />
        )}
        {/* A CTPS chega em cinco fotos e o processo precisa de um arquivo só: o
            PDF nasce aqui, na pasta do cliente, na ordem da conversa. */}
        {podeMandar && <Acao rotulo="Juntar as fotos em um PDF" icone={ICONES.peca} desligado={!quantas} aoTocar={juntarEmPdf} />}
        {podeMandar && (
          <Acao
            rotulo="Encaminhar as selecionadas"
            icone={ICONES.encaminhar}
            desligado={!quantas}
            aoTocar={abrirEncaminharVarias}
          />
        )}
        {podeApagar && (
          <Acao
            rotulo="Apagar para todos"
            icone={ICONES.apagar}
            perigo
            desligado={!quantas}
            aoTocar={async () => {
              if (
                await confirmar({
                  titulo: `Apagar ${quantas} ${quantas > 1 ? "mensagens" : "mensagem"} para todos?`,
                  texto: "Elas somem do WhatsApp do cliente também, e isso não se desfaz.",
                  acao: "Apagar",
                  perigo: true,
                })
              )
                apagarVarias();
            }}
          />
        )}
        <ButtonGroupSeparator className="bg-creme/20 data-vertical:h-5" />
        <Acao rotulo="Sair da seleção" icone={ICONES.fechar} aoTocar={sairDaSelecao} />
      </ButtonGroup>
    </div>
  );
}

/*
 * Desligado sobre a barra escura. A regra de desabilitado da casa (fora de
 * camada, `index.css`) pinta fundo claro apagado e letra de tinta — certo sobre
 * papel, e um retângulo cinza-claro no meio da barra escura aqui. O idioma é o
 * mesmo (fundo nenhum, borda tracejada, letra legível sem opacidade), no creme
 * da barra; no `style` porque é ele que vence aquela regra.
 */
const DESLIGADO_NO_ESCURO = {
  background: "transparent",
  color: "color-mix(in oklab, var(--color-creme) 55%, transparent)",
  border: "1px dashed color-mix(in oklab, var(--color-creme) 20%, transparent)",
};

function Acao({ rotulo, icone, aoTocar, desligado = false, perigo = false }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={aoTocar}
      disabled={desligado}
      data-dica={rotulo}
      aria-label={rotulo}
      /* A barra é escura, e isso são DUAS declarações — a que faltava é a da
         LETRA. O `hover:bg-muted` do preset acenderia um retângulo claro no meio
         dela (por isso o realce é creme com mais alfa, como no trilho), e o
         `hover:text-accent-foreground` que vem junto do `ghost` é a TINTA: sem
         `hover:text-creme` escrito, o ícone escurecia até sumir justamente
         quando o ponteiro encostava nele. É a mesma pegadinha do botão da
         petição e do trilho da lateral.

         Apagar é destrutivo, não terra: o terra é o que vai ao cliente, e aqui
         é o que some dele. */
      className={`size-padrao flex-none text-creme transition-[background-color,transform] duration-(--t-toque) ease-entra hover:text-creme dark:hover:text-creme ${
        perigo ? "bg-destructive/34 hover:bg-destructive/50" : "bg-creme/12 hover:bg-creme/20"
      }`}
      style={desligado ? DESLIGADO_NO_ESCURO : undefined}
    >
      <Icone de={icone} tamanho={16} />
    </Button>
  );
}
